import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  cloneTemplate,
  createTemplate,
  listTemplates,
  publishTemplate,
  retireTemplate,
  SectionRequest,
  TemplateRecord,
  TemplateRequest,
  updateTemplate,
} from './BusinessTemplateService';

// ─── constants ────────────────────────────────────────────────────────────────

const TEMPLATE_TYPES = ['START_UP', 'SMALL_BUSINESS', 'PRIVATE_EQUITY'];

const SECTION_TYPES = ['PROFILE', 'FINANCIAL', 'TEAM', 'OBJECTIVES', 'CUSTOM'];

type StatusTab = 'ALL' | 'DRAFT' | 'PUBLISHED' | 'RETIRED';

// ─── helpers ──────────────────────────────────────────────────────────────────

function typeColor(
  type: string,
): 'default' | 'primary' | 'success' | 'warning' {
  if (type === 'START_UP') return 'primary';
  if (type === 'SMALL_BUSINESS') return 'success';
  if (type === 'PRIVATE_EQUITY') return 'warning';
  return 'default';
}

function statusColor(
  status: string,
): 'default' | 'success' | 'error' {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'RETIRED') return 'error';
  return 'default';
}

// ─── Section row state ────────────────────────────────────────────────────────

interface SectionRow {
  title: string;
  sectionType: string;
  description: string;
  required: boolean;
}

function emptySection(): SectionRow {
  return { title: '', sectionType: 'CUSTOM', description: '', required: true };
}

// ─── EditDialog ───────────────────────────────────────────────────────────────

interface EditDialogProps {
  template: TemplateRecord | null; // null = create new
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function EditDialog({ template, open, onClose, onSaved }: EditDialogProps) {
  const isCreate = template === null;

  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState('START_UP');
  const [description, setDescription] = useState('');
  const [sections, setSections] = useState<SectionRow[]>([emptySection()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (template) {
        setName(template.name ?? '');
        setTemplateType(template.templateType ?? 'START_UP');
        setDescription(template.description ?? '');
        setSections(
          template.sections.length > 0
            ? template.sections.map((s) => ({
              title: s.title,
              sectionType: s.sectionType ?? 'CUSTOM',
              description: s.description ?? '',
              required: s.required,
            }))
            : [emptySection()],
        );
      } else {
        setName('');
        setTemplateType('START_UP');
        setDescription('');
        setSections([emptySection()]);
      }
      setError('');
    }
  }, [open, template]);

  function updateSection(index: number, field: keyof SectionRow, value: string | boolean) {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  }

  function addSection() {
    setSections((prev) => [...prev, emptySection()]);
  }

  function removeSection(index: number) {
    setSections((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    const req: TemplateRequest = {
      name,
      templateType,
      description: description || undefined,
      sections: sections.map((s, i): SectionRequest => ({
        title: s.title,
        sectionType: s.sectionType,
        description: s.description || undefined,
        sequenceOrder: i + 1,
        required: s.required,
      })),
    };
    try {
      if (isCreate) {
        await createTemplate(req);
      } else {
        await updateTemplate(template!.id, req);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not save template.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreate ? 'New Draft Template' : `Edit Template — ${template?.name}`}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          required
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Template Type</InputLabel>
          <Select
            value={templateType}
            label="Template Type"
            onChange={(e) => setTemplateType(e.target.value)}
          >
            {TEMPLATE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          sx={{ mb: 3 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Sections
        </Typography>

        {sections.map((s, i) => (
          <Box
            key={i}
            sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Box display="flex" gap={1} alignItems="flex-start">
              <TextField
                label="Title"
                value={s.title}
                onChange={(e) => updateSection(i, 'title', e.target.value)}
                size="small"
                sx={{ flex: 2 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={s.sectionType}
                  label="Type"
                  onChange={(e) => updateSection(i, 'sectionType', e.target.value)}
                >
                  {SECTION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => removeSection(i)}
                disabled={sections.length === 1}
                aria-label="Remove section"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={16}
                  height={16}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </IconButton>
            </Box>
            <Box display="flex" alignItems="center" mt={1} gap={2}>
              <TextField
                label="Description"
                value={s.description}
                onChange={(e) => updateSection(i, 'description', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={s.required}
                    onChange={(e) => updateSection(i, 'required', e.target.checked)}
                  />
                }
                label="Required"
              />
            </Box>
          </Box>
        ))}

        <Button size="small" variant="outlined" onClick={addSection} sx={{ mt: 1 }}>
          + Add Section
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : isCreate ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ReasonDialog ─────────────────────────────────────────────────────────────

interface ReasonDialogProps {
  open: boolean;
  title: string;
  warning: string;
  confirmLabel: string;
  confirmColor?: 'primary' | 'error' | 'warning';
  busy: boolean;
  error: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

function ReasonDialog({
  open,
  title,
  warning,
  confirmLabel,
  confirmColor = 'primary',
  busy,
  error,
  onConfirm,
  onClose,
}: ReasonDialogProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(reason)}
          variant="contained"
          color={confirmColor}
          disabled={busy}
        >
          {busy ? <CircularProgress size={18} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── BusinessTemplateAdminPage ────────────────────────────────────────────────

export default function BusinessTemplateAdminPage() {
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState<StatusTab>('ALL');

  // undefined = dialog closed, null = create new, TemplateRecord = edit existing
  const [editTarget, setEditTarget] = useState<TemplateRecord | null | undefined>(undefined);
  const [publishTarget, setPublishTarget] = useState<TemplateRecord | null>(null);
  const [retireTarget, setRetireTarget] = useState<TemplateRecord | null>(null);

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    listTemplates()
      .then((r) => setTemplates(r.data))
      .catch(() => setLoadError('Could not load templates.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = templates.filter((t) => tab === 'ALL' || t.status === tab);

  async function handlePublish(reason: string) {
    if (!publishTarget) return;
    setActionBusy(true);
    setActionError('');
    try {
      await publishTemplate(publishTarget.id, reason);
      setPublishTarget(null);
      load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? 'Could not publish template.');
    } finally {
      setActionBusy(false);
    }
  }

  async function handleRetire(reason: string) {
    if (!retireTarget) return;
    setActionBusy(true);
    setActionError('');
    try {
      await retireTemplate(retireTarget.id, reason);
      setRetireTarget(null);
      load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? 'Could not retire template.');
    } finally {
      setActionBusy(false);
    }
  }

  async function handleClone(template: TemplateRecord) {
    try {
      await cloneTemplate(template.id);
      load();
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? 'Could not clone template.');
    }
  }

  return (
    <AdminDashboard>
      <Container maxWidth="xl" sx={{ paddingY: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={700}>
            Business Template Configuration
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setEditTarget(null)}
          >
            New Template
          </Button>
        </Box>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" value="ALL" />
          <Tab label="Draft" value="DRAFT" />
          <Tab label="Published" value="PUBLISHED" />
          <Tab label="Retired" value="RETIRED" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : visible.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography color="text.secondary">No templates found.</Typography>
          </Box>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={2}>
            {visible.map((tmpl) => (
              <Box
                key={tmpl.id}
                sx={{
                  width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {/* Header row */}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box>
                    <Typography variant="body1" fontWeight={700}>
                      {tmpl.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tmpl.code}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    v{tmpl.version}
                  </Typography>
                </Box>

                {/* Chips row */}
                <Box display="flex" gap={0.75} flexWrap="wrap">
                  <Chip
                    label={tmpl.templateType.replace(/_/g, ' ')}
                    color={typeColor(tmpl.templateType)}
                    size="small"
                  />
                  <Chip
                    label={tmpl.status}
                    color={statusColor(tmpl.status)}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${tmpl.sectionCount} section${tmpl.sectionCount !== 1 ? 's' : ''}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {/* Description */}
                {tmpl.description && (
                  <Typography variant="caption" color="text.secondary">
                    {tmpl.description}
                  </Typography>
                )}

                {/* Actions */}
                <Box display="flex" gap={0.5} flexWrap="wrap" mt="auto" pt={1}>
                  {tmpl.status === 'DRAFT' && (
                    <>
                      <Button size="small" onClick={() => setEditTarget(tmpl)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        onClick={() => {
                          setActionError('');
                          setPublishTarget(tmpl);
                        }}
                      >
                        Publish
                      </Button>
                    </>
                  )}
                  {tmpl.status === 'PUBLISHED' && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setActionError('');
                        setRetireTarget(tmpl);
                      }}
                    >
                      Retire
                    </Button>
                  )}
                  <Button size="small" variant="outlined" onClick={() => handleClone(tmpl)}>
                    Clone
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    href="/admin/audit-log?targetType=BusinessTemplate"
                  >
                    Audit
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Container>

      {/* Edit / Create dialog */}
      {editTarget !== undefined && (
        <EditDialog
          template={editTarget}
          open={editTarget !== undefined}
          onClose={() => setEditTarget(undefined)}
          onSaved={load}
        />
      )}

      {/* Publish dialog */}
      <ReasonDialog
        open={publishTarget !== null}
        title={`Publish "${publishTarget?.name}"`}
        warning="This will retire any existing Published version for the same type."
        confirmLabel="Publish"
        confirmColor="primary"
        busy={actionBusy}
        error={actionError}
        onConfirm={handlePublish}
        onClose={() => setPublishTarget(null)}
      />

      {/* Retire dialog */}
      <ReasonDialog
        open={retireTarget !== null}
        title={`Retire "${retireTarget?.name}"`}
        warning="This template will no longer be available for new use. Existing assignments are unaffected."
        confirmLabel="Retire"
        confirmColor="error"
        busy={actionBusy}
        error={actionError}
        onConfirm={handleRetire}
        onClose={() => setRetireTarget(null)}
      />
    </AdminDashboard>
  );
}
